import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setJwtToken, getJwtToken } from "../utils/session";
import { apiProfile } from "../api/api";

/* --------- Config --------- */
const PROCESS_ENV =
  typeof process !== "undefined" && process?.env ? process.env : undefined;

const FULLMARGIN_URL =
  import.meta?.env?.VITE_FULLMARGIN_URL ||
  PROCESS_ENV?.VITE_FULLMARGIN_URL ||
  "https://fullmargin.net";

/* --------- Utils --------- */
function extractTokenAndDest(search) {
  const params = new URLSearchParams(search || "");
  let token = params.get("token") || "";
  let dest = params.get("next") || "/";

  if (!token && dest) {
    try {
      const decoded = decodeURIComponent(dest);
      const m = decoded.match(/[?&#]token=([^&]+)/i);
      if (m) token = m[1];
    } catch {
      // Ignore les URL mal encodées et continue avec la valeur par défaut.
    }
  }

  try {
    const urlLike = decodeURIComponent(dest || "/");
    const qIdx = urlLike.indexOf("?");

    if (qIdx >= 0) {
      const base = urlLike.slice(0, qIdx) || "/";
      const qs = new URLSearchParams(urlLike.slice(qIdx + 1));
      qs.delete("token");
      dest = qs.toString() ? `${base}?${qs.toString()}` : base;
    } else {
      dest = urlLike || "/";
    }
  } catch {
    dest = "/";
  }

  return { token, dest };
}

/* --------- UI --------- */
function FullMarginMark() {
  return (
    <div
      className="grid h-12 w-12 place-items-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm transition-colors duration-300 dark:bg-slate-950 dark:ring-white/10 dark:shadow-[0_12px_32px_rgba(0,0,0,.35)]"
      aria-label="FullMargin"
    >
      <img
        src="/logo192.png"
        alt="FullMargin"
        className="h-9 w-9 object-contain"
        draggable={false}
        decoding="async"
        loading="eager"
      />
    </div>
  );
}

function OrbitRing({ spinning = true, size = 110 }) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const arc = c * 0.3;
  const gap = c - arc;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Anneau fixe */}
      <svg
        className="absolute inset-0 text-slate-200/95 transition-colors duration-300 dark:text-slate-700/80"
        width={size}
        height={size}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
        />
      </svg>

      {/* Arc animé */}
      <svg
        className={`absolute inset-0 text-violet-700 transition-colors duration-300 dark:text-violet-400 ${
          spinning ? "fm-orbit-spin" : ""
        }`}
        width={size}
        height={size}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arc} ${gap}`}
        />
      </svg>

      <div className="absolute inset-0 grid place-items-center">
        <FullMarginMark />
      </div>
    </div>
  );
}

/* --------- Page --------- */
export default function SSOPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = useMemo(
    () => extractTokenAndDest(location.search),
    [location.search]
  );

  const [state, setState] = useState("connecting"); // connecting | ok | fail

  useEffect(() => {
    document.body.dataset.hideChrome = "true";
    return () => {
      delete document.body.dataset.hideChrome;
    };
  }, []);

  const proceed = useCallback(async () => {
    setState("connecting");

    const { token, dest } = parsed;

    if (token) {
      setJwtToken(token);
    }

    if (!token && !getJwtToken()) {
      setState("fail");
      return;
    }

    try {
      const me = await apiProfile();

      if (me?.error || me?.status === 401) {
        setState("fail");
        return;
      }

      setState("ok");
      requestAnimationFrame(() => navigate(dest || "/", { replace: true }));
    } catch {
      setState("ok");
      requestAnimationFrame(() =>
        navigate(parsed.dest || "/", { replace: true })
      );
    }
  }, [navigate, parsed]);

  useEffect(() => {
    proceed();
  }, [proceed]);

  const onRetry = (event) => {
    event?.preventDefault?.();
    proceed();
  };

  const onGoFullMargin = () => {
    const next = `${window.location.origin}/auth/sso/callback`;
    const url = `${FULLMARGIN_URL}?next=${encodeURIComponent(next)}`;
    window.location.href = url;
  };

  const spinRing = state !== "ok";

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden bg-slate-50 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100">
      {/* Fond animé */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(124,58,237,.10),transparent_34%),linear-gradient(180deg,rgba(255,255,255,.92),rgba(248,250,252,.96))] transition-opacity duration-300 dark:bg-[radial-gradient(circle_at_top,rgba(139,92,246,.18),transparent_36%),linear-gradient(180deg,rgba(2,6,23,.98),rgba(15,23,42,.96))]" />
        <div className="sideBlob sideBlobLeft" />
        <div className="sideBlob sideBlobRight" />
      </div>

      <div className="relative grid min-h-[100dvh] place-items-center px-4 py-8 sm:px-6">
        <div className="w-full max-w-[520px]">
          <div className="rounded-[28px] border border-slate-200/80 bg-white/88 p-7 shadow-[0_18px_60px_rgba(2,6,23,.10)] backdrop-blur-xl transition-colors duration-300 dark:border-white/10 dark:bg-slate-900/78 dark:shadow-[0_24px_80px_rgba(0,0,0,.38)] sm:rounded-[32px] sm:p-10">
            <div className="flex justify-center">
              <OrbitRing spinning={spinRing} size={116} />
            </div>

            <h1 className="mt-8 text-center text-xl font-semibold tracking-[-0.01em] text-slate-950 dark:text-white">
              {state === "fail" ? t("sso.headingFail") : t("sso.headingLoading")}
            </h1>

            {state === "fail" ? (
              <p className="mt-2 text-center text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t("sso.messageFail")}
              </p>
            ) : (
              <p className="mt-2 text-center text-sm leading-6 text-slate-500 dark:text-slate-400">
                {t("sso.messageLoading")}
              </p>
            )}

            <div className="mt-7 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={onGoFullMargin}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-violet-700 px-4 py-3 font-semibold text-white shadow-[0_12px_36px_rgba(109,40,217,.28)] transition hover:bg-violet-800 active:scale-[0.99] dark:bg-violet-500 dark:shadow-[0_16px_40px_rgba(139,92,246,.28)] dark:hover:bg-violet-400"
              >
                {t("sso.openFullMargin")}
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M13 5l7 7-7 7M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                type="button"
                onClick={onRetry}
                className="text-sm font-medium text-slate-600 underline underline-offset-4 transition hover:text-slate-950 dark:text-slate-400 dark:hover:text-white"
              >
                {t("sso.retry")}
              </button>
            </div>
          </div>

          <p className="mt-5 text-center text-xs text-slate-400 dark:text-slate-500">
            {t("sso.autoRedirect")}
          </p>
        </div>
      </div>

      <style>{`
        .fm-orbit-spin {
          transform-origin: 50% 50%;
          transform-box: fill-box;
          animation: fmOrbitSpin 1.05s linear infinite;
        }

        @keyframes fmOrbitSpin {
          to {
            transform: rotate(360deg);
          }
        }

        .sideBlob {
          position: absolute;
          top: 50%;
          width: 520px;
          height: 520px;
          border-radius: 999px;
          filter: blur(48px);
          opacity: .22;
          transform: translateY(-50%);
          background:
            radial-gradient(circle at 30% 30%, rgba(109,40,217,.9), rgba(109,40,217,0) 60%),
            radial-gradient(circle at 70% 70%, rgba(236,72,153,.75), rgba(236,72,153,0) 60%);
          animation: floatY 6.5s ease-in-out infinite;
        }

        .dark .sideBlob {
          opacity: .18;
          filter: blur(56px);
          background:
            radial-gradient(circle at 30% 30%, rgba(139,92,246,.85), rgba(139,92,246,0) 62%),
            radial-gradient(circle at 70% 70%, rgba(168,85,247,.62), rgba(168,85,247,0) 62%);
        }

        .sideBlobLeft {
          left: -320px;
          animation-delay: 0s;
        }

        .sideBlobRight {
          right: -320px;
          animation-delay: -2s;
        }

        @keyframes floatY {
          0%,
          100% {
            transform: translateY(-50%) translateX(0) scale(1);
          }

          50% {
            transform: translateY(calc(-50% - 28px)) translateX(18px) scale(1.03);
          }
        }

        @media (max-width: 640px) {
          .sideBlob {
            width: 360px;
            height: 360px;
            opacity: .16;
          }

          .sideBlobLeft {
            left: -250px;
          }

          .sideBlobRight {
            right: -250px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sideBlob,
          .fm-orbit-spin {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
